// Dashboard card configuration and role-based access control

export interface DashboardCard {
    key: string;
    label: string;
    category: 'stats' | 'quickAccess' | 'activity' | 'student';
    roles: string[];
    defaultVisible: boolean;
}

export const dashboardCards: DashboardCard[] = [
    // Statistics Cards
    {
        key: 'myCourses',
        label: 'Meine Kurse',
        category: 'stats',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'inProgress',
        label: 'In Bearbeitung',
        category: 'stats',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'completed',
        label: 'Abgeschlossen',
        category: 'stats',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'learningProgress',
        label: 'Gesamtfortschritt',
        category: 'stats',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'certificates',
        label: 'Zertifikate',
        category: 'stats',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'wikiArticles',
        label: 'Aktuelles & Wissen',
        category: 'stats',
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_CONTENT_MANAGER', 'ROLE_PLATFORM_ADMIN', 'ROLE_EDITOR'],
        defaultVisible: true
    },
    {
        key: 'media',
        label: 'Medienbibliothek',
        category: 'stats',
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_CONTENT_MANAGER', 'ROLE_INSTRUCTOR', 'ROLE_MEDIA_MANAGER', 'ROLE_PLATFORM_ADMIN'],
        defaultVisible: true
    },

    // Quick Access Cards
    {
        key: 'quickAccessMyCourses',
        label: 'Schnellzugriff: Meine Kurse',
        category: 'quickAccess',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        // Issue #153: NUR Admin-/Content-Rollen — quickAccessCatalogs leitet zur
        // KursVERWALTUNG (/courses/catalogs), die Studenten nicht sehen duerfen.
        key: 'quickAccessCatalogs',
        label: 'Schnellzugriff: Kursverwaltung',
        category: 'quickAccess',
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_CONTENT_MANAGER', 'ROLE_INSTRUCTOR', 'ROLE_PLATFORM_ADMIN'],
        defaultVisible: true
    },
    {
        // Issue #153: Lerner-Katalog (/catalog) — fuer Studenten zum Finden +
        // Buchen neuer Kurse. Distinkt von der Kursverwaltung darueber.
        key: 'quickAccessCatalog',
        label: 'Schnellzugriff: Kurskatalog',
        category: 'quickAccess',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'quickAccessWiki',
        label: 'Schnellzugriff: Aktuelles & Wissen',
        category: 'quickAccess',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'quickAccessMedia',
        label: 'Schnellzugriff: Medienbibliothek',
        category: 'quickAccess',
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_CONTENT_MANAGER', 'ROLE_INSTRUCTOR', 'ROLE_MEDIA_MANAGER', 'ROLE_PLATFORM_ADMIN'],
        defaultVisible: true
    },

    // Activity Cards
    {
        key: 'activityMostViewed',
        label: 'Meistgesehene Artikel',
        category: 'activity',
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_CONTENT_MANAGER', 'ROLE_PLATFORM_ADMIN', 'ROLE_EDITOR'],
        defaultVisible: true
    },
    {
        key: 'activityRecentArticles',
        label: 'Neueste Artikel',
        category: 'activity',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'activityRecentFiles',
        label: 'Neueste Dateien',
        category: 'activity',
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_CONTENT_MANAGER', 'ROLE_INSTRUCTOR', 'ROLE_MEDIA_MANAGER', 'ROLE_PLATFORM_ADMIN'],
        defaultVisible: true
    },

    // Student-specific Cards — Lern-Dashboard-Sektion
    {
        key: 'mandatoryAssignments',
        label: 'Pflichtaufgaben',
        category: 'student',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'weeklyProgress',
        label: 'Wochenfortschritt',
        category: 'student',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'learningPaths',
        label: 'Lernpfade',
        category: 'student',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'gamification',
        label: 'Serie & Erfolge',
        category: 'student',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'pendingReviews',
        label: 'Ausstehende Reviews',
        category: 'student',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'myLearningGroups',
        label: 'Meine Lerngruppen',
        category: 'student',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
    {
        key: 'recommendedCourses',
        label: 'Empfohlene Kurse',
        category: 'student',
        roles: ['ROLE_USER'],
        defaultVisible: true
    },
];

export const getCategoryTitle = (category: string): string => {
    switch (category) {
        case 'stats':
            return 'Statistik-Karten';
        case 'quickAccess':
            return 'Schnellzugriff';
        case 'activity':
            return 'Aktivitäten';
        case 'student':
            return 'Lern-Dashboard';
        default:
            return category;
    }
};

export const getCardsForRole = (roles: string[]): DashboardCard[] => {
    return dashboardCards.filter(card =>
        card.roles.some(role => roles.includes(role))
    );
};

export const getDefaultPreferencesForRole = (roles: string[]): { [key: string]: boolean } => {
    const availableCards = getCardsForRole(roles);
    const preferences: { [key: string]: boolean } = {};

    availableCards.forEach(card => {
        preferences[card.key] = card.defaultVisible;
    });

    return preferences;
};

export const getDefaultCardOrder = (roles: string[]): string[] => {
    return getCardsForRole(roles).map(card => card.key);
};

export const mergeCardOrder = (savedOrder: string[] | null, roles: string[]): string[] => {
    const availableCards = getCardsForRole(roles);
    const saved = savedOrder ?? [];

    return Array.from(new Set(availableCards.map(card => card.category))).flatMap(category => {
        const keys = availableCards.filter(card => card.category === category).map(card => card.key);
        const ordered = Array.from(new Set(saved.filter(key => keys.includes(key))));

        return [...ordered, ...keys.filter(key => !ordered.includes(key))];
    });
};

export const isCardAvailableForRole = (cardKey: string, roles: string[]): boolean => {
    const card = dashboardCards.find(c => c.key === cardKey);
    if (!card) return false;
    return card.roles.some(role => roles.includes(role));
};
